import {
  AUTH_EMAIL_DOMAIN,
  PASSWORD_MIN,
  ROLE_LABELS,
  ROLE_PAGES,
  USERNAME_MIN,
  type Page,
  type Role,
} from './constants';

/** Prototype `roleLabel(role)` — unknown roles fall back to "Staf". */
export function roleLabel(role: string | null | undefined): string {
  return (ROLE_LABELS as Record<string, string>)[role ?? ''] ?? 'Staf';
}

/** Prototype `initials(name)` — two uppercase letters, defaults to "AG". */
export function initials(name: string | null | undefined): string {
  const p = String(name || 'AG')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return ((p[0]?.[0] || 'A') + (p[1]?.[0] || p[0]?.[1] || 'G')).toUpperCase();
}

/** Prototype `canAccessPage(page)` via ROLE_PAGES. */
export function canAccessPage(role: Role | null | undefined, page: string): boolean {
  if (!role) return false;
  return (ROLE_PAGES[role] ?? ROLE_PAGES.staff).includes(page as Page);
}

/** Prototype `defaultPageForRole()`: the dashboard, or the first page a role may open when it has none. */
export function defaultPageForRole(role?: Role): Page {
  if (role === undefined || ROLE_PAGES[role].includes('dashboard')) return 'dashboard';
  return ROLE_PAGES[role][0] ?? 'dashboard';
}

export function normalizeUsername(raw: string | null | undefined): string {
  return String(raw ?? '')
    .trim()
    .toLowerCase();
}

const USERNAME_RE = /^[a-z0-9._-]+$/;

/** Returns the prototype's toast text when the username is invalid, else null. */
export function usernameError(username: string): string | null {
  if (username.length < USERNAME_MIN) return 'Username duhet t’i ketë së paku 3 karaktere';
  if (!USERNAME_RE.test(username))
    return 'Username mund të përmbajë vetëm shkronja, numra, pikë, vizë dhe nënvizë';
  return null;
}

/** Returns the prototype's toast text when the password is invalid, else null. */
export function passwordError(password: string): string | null {
  if (password.length < PASSWORD_MIN) return 'Fjalëkalimi duhet t’i ketë së paku 4 karaktere';
  return null;
}

/** Username → synthetic e-mail used only inside Supabase Auth. */
export function usernameToEmail(username: string): string {
  return `${normalizeUsername(username)}@${AUTH_EMAIL_DOMAIN}`;
}

export function emailToUsername(email: string | null | undefined): string {
  const e = String(email ?? '');
  const suffix = `@${AUTH_EMAIL_DOMAIN}`;
  return e.endsWith(suffix) ? e.slice(0, -suffix.length) : e;
}

export const ADMIN_ONLY_ACTIONS = [
  'users.manage',
  'tasks.create',
  'tasks.assign',
  'tasks.delete',
  'employees.manage',
  'leaves.manage',
  'settings.manage',
  'requests.delete',
  'rotation.edit',
] as const;
export type AdminAction = (typeof ADMIN_ONLY_ACTIONS)[number];

/** Action-level permission check (prototype enforces per action, not per page): listed actions are admin-only. */
export function can(role: Role | null | undefined, action: AdminAction): boolean {
  if (!(ADMIN_ONLY_ACTIONS as readonly string[]).includes(action)) return false;
  return role === 'admin';
}
